import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { UPDATE_USER, UpdateUserMutation, UpdateUserMutationVariables } from '@neo.tax/shared/graphql';
import { useCurrentUser } from '@neo.tax/shared/hooks';
import { useAnalyticId } from '@neo.tax/shared/state';
import { validateFullName } from '@neo.tax/shared/utils';

interface ProfileFormValues {
  fullName: string;
}

interface UseProfileEditProps {
  userId: string;
  onSuccess?: () => void;
}

interface UseProfileEditReturn {
  control: ReturnType<typeof useForm<ProfileFormValues>>['control'];
  formState: ReturnType<typeof useForm<ProfileFormValues>>['formState'];
  handleSubmit: ReturnType<typeof useForm<ProfileFormValues>>['handleSubmit'];
  onSubmit: (data: ProfileFormValues) => Promise<void>;
  isLoading: boolean;
  user: ReturnType<typeof useCurrentUser>['user'];
  updateProfileFullNameAnalyticId: string;
  validateFullName: typeof validateFullName;
}

export const useProfileEdit = ({ onSuccess, userId }: UseProfileEditProps): UseProfileEditReturn => {
  const updateProfileFullNameAnalyticId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_UPDATE_PROFILE_FULL_NAME);

  const [updateUser, { loading: isLoading }] = useMutation<UpdateUserMutation, UpdateUserMutationVariables>(
    UPDATE_USER,
  );
  const { user } = useCurrentUser();

  const { control, formState, handleSubmit, reset } = useForm<ProfileFormValues>({
    mode: 'onChange',
    defaultValues: {
      fullName: '',
    },
  });

  useEffect(() => {
    if (user?.fullName) {
      reset({
        fullName: user.fullName,
      });
    }
  }, [user?.fullName, reset]);

  const onSubmit = async (data: ProfileFormValues): Promise<void> => {
    try {
      await updateUser({ variables: { userId, input: { fullName: data.fullName } } });
      onSuccess?.();
    } catch (err) {
      console.error('Failed to update profile:', err);
    }
  };

  return {
    control,
    formState,
    handleSubmit,
    onSubmit,
    isLoading,
    user,
    updateProfileFullNameAnalyticId,
    validateFullName,
  };
};
